import { routeSampleRate } from "./fayin-pcm.js";

/** An attempt being recorded. */
export interface Recording {
  /** Stop, and hand back what was said. */
  readonly stop: () => Promise<AudioBuffer>;
}

/** Whether the site may use the microphone, as the browser says before it asks. */
export type MicrophoneState = "granted" | "prompt" | "denied";

/**
 * Whether the microphone is allowed, without asking for it.
 *
 * The Permissions API answers this in every browser the site supports, Safari
 * from 16. A browser that cannot answer is taken as one that will ask.
 */
export async function microphoneState(): Promise<MicrophoneState> {
  try {
    const status = await navigator.permissions.query({
      name: "microphone",
    });

    return status.state;
  } catch {
    return "prompt";
  }
}

/** Why the microphone could not be opened: refused, or there is none. */
export type MicrophoneRefusal = "denied" | "missing";

/** What a failure to open the microphone means for the learner. */
export function refusalOf(error: unknown): MicrophoneRefusal {
  return error instanceof Error &&
    (error.name === "NotFoundError" || error.name === "OverconstrainedError")
    ? "missing"
    : "denied";
}

/**
 * The microphone, opened once and kept open while a drill runs.
 *
 * **Open for the drill, not for each attempt.** Asking for the stream on
 * every press took long enough that a learner could not tell when recording
 * began, so Hugh held the button, waited, spoke, waited and let go
 * (2026-10-26). With the stream open a press starts a recorder at once, and
 * the browser's own recording indicator stays on while the drill runs.
 * `close` releases it at the end of a drill and when the page goes away.
 *
 * **MediaRecorder, then decoded, rather than a worklet.** A worklet module is
 * a script the site policy would have to admit by its address, and the
 * compressed recording each browser makes (Opus in Chrome and Firefox, AAC in
 * Safari) decodes through `decodeAudioData` in the same browser.
 */
export class Microphone {
  #stream: Promise<MediaStream> | undefined;

  /**
   * Open the microphone, asking the learner if the browser has to.
   * @throws When the learner refuses it or there is none.
   */
  async open(): Promise<void> {
    await this.#opened();
  }

  /**
   * Start recording, opening the microphone first if it is not open.
   * @throws When the microphone cannot be opened or recorded from.
   */
  async start(): Promise<Recording> {
    const stream = await this.#opened();
    const chunks: Blob[] = [];
    let recorder: MediaRecorder;

    try {
      recorder = new MediaRecorder(stream);
      recorder.addEventListener("dataavailable", (event) => {
        chunks.push(event.data);
      });
      recorder.start();
    } catch (error) {
      // A stream no recorder can take is no use to the next press either.
      this.close();
      throw error;
    }

    return {
      stop: async () => {
        const stopped = new Promise<void>((resolve) => {
          recorder.addEventListener(
            "stop",
            () => {
              resolve();
            },
            { once: true },
          );
        });

        recorder.stop();
        await stopped;

        return decoded(new Blob(chunks, { type: recorder.mimeType }));
      },
    };
  }

  /** Release the microphone, if it is open. */
  close(): void {
    const stream = this.#stream;

    this.#stream = undefined;
    void stream?.then(
      (open) => {
        for (const track of open.getTracks()) {
          track.stop();
        }
      },
      () => undefined,
    );
  }

  /** The open stream, asked for once. A refusal is not kept, so it can be asked again. */
  async #opened(): Promise<MediaStream> {
    this.#stream ??= navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    try {
      return await this.#stream;
    } catch (error) {
      this.#stream = undefined;
      throw error;
    }
  }
}

/** The page's one microphone. */
export const microphone = new Microphone();

/**
 * Start recording from the page's microphone.
 * @throws When the learner refuses the microphone or there is none.
 */
export async function startRecording(): Promise<Recording> {
  return microphone.start();
}

/** A recorded blob as samples. */
async function decoded(blob: Blob): Promise<AudioBuffer> {
  const context = new AudioContext();

  try {
    return await context.decodeAudioData(await blob.arrayBuffer());
  } finally {
    void context.close();
  }
}

/** One channel of a recording at the rate the route takes. */
export async function resampled(buffer: AudioBuffer): Promise<Float32Array> {
  const offline = new OfflineAudioContext(
    1,
    Math.max(1, Math.ceil(buffer.duration * routeSampleRate)),
    routeSampleRate,
  );
  const source = offline.createBufferSource();

  source.buffer = buffer;
  source.connect(offline.destination);
  source.start();

  return (await offline.startRendering()).getChannelData(0);
}

/** Something playing, for a picture to follow. */
export interface Playing {
  /** Seconds played so far. */
  readonly elapsed: () => number;

  /** Settles when it has finished, been stopped or failed to start. */
  readonly ended: Promise<void>;

  /** Stop it early. */
  readonly stop: () => void;
}

/**
 * Play a recording on the site.
 *
 * A browser that refuses to play before the learner has pressed anything
 * (a word that comes on screen as the page loads) ends at once, and the
 * learner presses Listen instead.
 */
export function playAddress(source: string): Playing {
  const element = new Audio(source);
  const ended = new Promise<void>((resolve) => {
    for (const name of ["ended", "error", "pause"]) {
      element.addEventListener(name, () => {
        resolve();
      });
    }

    element.play().catch(() => {
      resolve();
    });
  });

  return {
    elapsed: () => element.currentTime,
    ended,
    stop: () => {
      element.pause();
    },
  };
}

/**
 * Play the learner's own recording back.
 *
 * Through Web Audio and not an `<audio>` element, since a recording's object
 * URL is a `blob:` address the site policy gives media no leave to load.
 * @param samples - Mono samples, -1 to 1.
 * @param sampleRate - Samples a second.
 */
export function playSamples(
  samples: Float32Array,
  sampleRate: number,
): Playing {
  const context = new AudioContext();
  const buffer = context.createBuffer(
    1,
    Math.max(samples.length, 1),
    sampleRate,
  );
  const source = context.createBufferSource();

  buffer.getChannelData(0).set(samples);
  source.buffer = buffer;
  source.connect(context.destination);

  const ended = new Promise<void>((resolve) => {
    source.addEventListener("ended", () => {
      resolve();
    });
  }).finally(() => {
    void context.close();
  });

  const startedAt = context.currentTime;

  source.start();

  return {
    elapsed: () => context.currentTime - startedAt,
    ended,
    stop: () => {
      try {
        source.stop();
      } catch {
        // Already finished.
      }
    },
  };
}

/**
 * A recording on the site, at the rate the route takes, for drawing its
 * picture. Fetched from the same origin, which `connect-src 'self'` allows.
 */
export async function loadedSamples(source: string): Promise<Float32Array> {
  const response = await fetch(source);
  const context = new OfflineAudioContext(1, 1, routeSampleRate);

  return resampled(await context.decodeAudioData(await response.arrayBuffer()));
}
